import { useEffect, useCallback, useState } from 'react';
import { getCSSPropertyValue } from 'utils/helpers';

/**
 * Custom hook that calculates and returns the left and right padding for a carousel container
 * based on its current X offset and padding values.
 *
 * @param {React.MutableRefObject<HTMLDivElement | null>} containerRef - A reference to the carousel container element.
 * @returns {Object} An object containing the calculated left and right padding values for the carousel.
 * @returns {string} return.carouselPaddingLeft - The calculated left padding for the carousel.
 * @returns {string} return.carouselPaddingRight - The calculated right padding for the carousel.
 *
 * @example
 * const containerRef = useRef<HTMLDivElement | null>(null);
 * const { carouselPaddingLeft, carouselPaddingRight } = useCarousel(containerRef);
 */
const useCarouselPadding = (
  containerRef: React.MutableRefObject<HTMLDivElement | null>,
) => {
  const [carouselXOffset, setCarouselXOffset] = useState(0);

  const updateCarouselXOffset = useCallback(() => {
    const containerXPosition =
      containerRef?.current?.getBoundingClientRect()?.x;

    setCarouselXOffset(containerXPosition ?? 0);
  }, [containerRef]);

  useEffect(() => {
    updateCarouselXOffset();
  }, [updateCarouselXOffset]);

  useEffect(() => {
    window.addEventListener('resize', updateCarouselXOffset);

    return () => {
      window.removeEventListener('resize', updateCarouselXOffset);
    };
  }, [containerRef, updateCarouselXOffset]);

  const calculatePaddingOffset = (side: 'left' | 'right') => {
    const containerPadding =
      getCSSPropertyValue(containerRef.current, `padding-${side}`) || '0';

    return `calc(${carouselXOffset}px + ${containerPadding})`;
  };

  const carouselPaddingLeft = calculatePaddingOffset('left');
  const carouselPaddingRight = calculatePaddingOffset('right');

  return { carouselPaddingLeft, carouselPaddingRight };
};

export default useCarouselPadding;
